import { isNil } from "lodash";
import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";
import { ReactNode } from "react";
import { generateId } from "../../utils";

export const TAG_TYPES = Object.freeze({
    PRIMARY: "PRIMARY",
    SECONDARY: "SECONDARY",
    NEUTRAL: "NEUTRAL",
    SUCCESS: "SUCCESS",
    WARNING: "WARNING",
    ERROR: "ERROR",
    INFO: "INFO",
    TERTIARY: "TERTIARY",
} as const);

export type TagType = (typeof TAG_TYPES)[keyof typeof TAG_TYPES];

export const TAG_SIZES = Object.freeze({
    SM: "SM",
    MD: "MD",
} as const);

export type TagSize = (typeof TAG_SIZES)[keyof typeof TAG_SIZES];

const SIZE_CLASSNAMES = Object.freeze({
    [TAG_SIZES.SM]: "h-5 px-1.5 text-xxs",
    [TAG_SIZES.MD]: "h-6 px-2 text-xs",
} as const);

const TYPE_CLASSNAMES = Object.freeze({
    [TAG_TYPES.PRIMARY]: {
        solid: "bg-primary-500 text-primary-text",
        soft: "bg-primary-75 text-primary-750",
    },
    [TAG_TYPES.SECONDARY]: {
        solid: "bg-secondary-500 text-secondary-text",
        soft: "bg-secondary-75 text-secondary-750",
    },
    [TAG_TYPES.NEUTRAL]: {
        solid: "bg-surface-550 text-text-300",
        soft: "bg-surface-520 text-text-500",
    },
    [TAG_TYPES.SUCCESS]: {
        solid: "bg-success-500 text-success-text",
        soft: "bg-success-75 text-success-750",
    },
    [TAG_TYPES.WARNING]: {
        solid: "bg-warning-500 text-warning-text",
        soft: "bg-warning-75 text-warning-750",
    },
    [TAG_TYPES.ERROR]: {
        solid: "bg-error-500 text-error-text",
        soft: "bg-error-75 text-error-750",
    },
    [TAG_TYPES.INFO]: {
        solid: "bg-info-500 text-info-text",
        soft: "bg-info-75 text-info-750",
    },
    [TAG_TYPES.TERTIARY]: {
        solid: "bg-tertiary-500 text-tertiary-text",
        soft: "bg-tertiary-75 text-tertiary-750",
    },
} as const);

export interface TagProps {
    type?: TagType;
    size?: TagSize;
    solid?: boolean;
    label?: ReactNode;
    tooltip?: ReactNode;
    disabled?: boolean;
    className?: string;
}

const Tag = (props: TagProps) => {
    const {
        type = TAG_TYPES.PRIMARY,
        size = TAG_SIZES.MD,
        solid = false,
        label,
        tooltip,
        disabled,
        className,
    } = props;

    const tooltipTargetClass = generateId("tag-tooltip");
    const hasTooltip = !isNil(tooltip);

    return (
        <>
            {hasTooltip && (
                <Tooltip
                    target={`.${tooltipTargetClass}`}
                    position="top"
                    mouseTrack
                    mouseTrackTop={15}
                    className="max-w-sm"
                    pt={{ text: { className: "wrap-break-word" } }}
                >
                    {tooltip}
                </Tooltip>
            )}
            <span
                className={classNames(
                    "inline-flex w-fit min-w-0 items-center gap-1.5 rounded font-semibold whitespace-nowrap",
                    SIZE_CLASSNAMES[size],
                    TYPE_CLASSNAMES[type][solid ? "solid" : "soft"],
                    disabled && "opacity-60",
                    hasTooltip && tooltipTargetClass,
                    className
                )}
            >
                <span className="min-w-0 truncate leading-4">{label}</span>
            </span>
        </>
    );
};

export default Tag;
